/**
 * Shared affiliate presentation (TMMPDM-0042). Status, plan and money render the server's own
 * fields. Nothing here infers a hold, a verdict or a total.
 */
import { Stack, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import type {
  AffiliateAgreementView,
  AffiliatePerformance,
  AffiliateStatus,
  CommissionPlanRef,
} from '@/api/tmm/types';
import { NoncompliantChip, ResellerCardStatusChip } from '../resellers/ResellerCardChips';
import { StatusChip } from '@/status';

const STATUS_FALLBACK: Record<AffiliateStatus, string> = {
  APPLIED: 'Applied',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  ENDED: 'Ended',
};

function affiliateStatusLabel(
  status: AffiliateStatus,
  t: (key: string, fallback?: string) => string,
): string {
  return t(`affiliates.status.${status}`, STATUS_FALLBACK[status]);
}

export function AffiliateStatusChip({ status }: { status: AffiliateStatus }) {
  const { t } = useCopy();
  const label = affiliateStatusLabel(status, t);
  return <StatusChip status={status} label={label} />;
}

/** The plan ref. A name and the server's status — not a verdict of our own. */
export function CommissionPlanLabel({
  plan,
  omitName = false,
}: {
  plan: CommissionPlanRef;
  /** The caller already shows the name (a link). Chips only. */
  omitName?: boolean;
}) {
  const { t } = useCopy();
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
      {omitName ? null : <span>{plan.name}</span>}
      <ResellerCardStatusChip status={plan.status} />
      <NoncompliantChip
        card={plan}
        explanation={t(
          'affiliates.plan.noncompliant',
          "Over a limit. It can't be assigned to more affiliates; affiliates on it keep it.",
        )}
      />
    </Stack>
  );
}

/** List-row plan: an absent agreement says so; a present one always names its plan. */
export function AgreementPlanCell({
  agreement,
}: {
  agreement?: AffiliateAgreementView | undefined;
}) {
  const { t } = useCopy();
  if (agreement === undefined) return t('affiliates.plan.noAgreement', 'No agreement');
  return <CommissionPlanLabel plan={agreement.commissionPlan} />;
}

/** One line per currency. An empty list is an em dash, never a zero we invented. */
export function Last30Lines({ rows }: { rows: AffiliatePerformance[] }) {
  const { t } = useCopy();
  if (rows.length === 0) return t('affiliates.absent', '—');
  return (
    <Stack spacing={0.25}>
      {rows.map((row) => (
        <Typography key={row.currency} variant="body2" component="div">
          <Amount minor={row.referredRevenue30dMinor} currency={row.currency} />{' '}
          {t('affiliates.last30.revenue', 'revenue')}
          {' · '}
          <Amount minor={row.commission30dMinor} currency={row.currency} />{' '}
          {t('affiliates.last30.commission', 'commission')}
        </Typography>
      ))}
    </Stack>
  );
}

export function HeldForReviewChip() {
  const { t } = useCopy();
  return <StatusChip tone="warning" label={t('affiliates.held.chip', 'Held for review')} />;
}
